import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { BasicModal } from "@life-line-lab/components-react";
import { isEmpty } from "lodash";
import { map } from "lodash";
import { Button } from "@life-line-lab/components-react";
import { DataTable, SelectInput } from "@life-line-lab/components-react";
import { Row, Col } from "antd";
import { CloseCircleOutlined } from "@ant-design/icons";
import useSession from "Hooks/session.hook";
import useAccessControlList from "Hooks/access-control.hook";
import { getStatusStyle } from "Helpers/get-status-colours";
import { toCapitalisedSpaced } from "Helpers/to-capitalised-spaced";
import AssignDriverBoxesModal from "Components/delivery-management/parcel-tracking-modals/sorting-modal/assign-driver-boxes-modal";

export const HandoverPointModal = ({
  orders,
  handoverOptions,
  handleSubmit,
  handleCancel,
  setSelectedRows,
}) => {
  const { t } = useTranslation();

  const [selectedHandoverPoint, setSelectedHandoverPoint] = useState<any>("");

  const handleSelectChange = (value: string | string[], name: string) => {
    setSelectedHandoverPoint(value);
  };

  const onSubmit = () => {
    const payload = {
      orderIDs: orders.map((order) => {
        return order._id;
      }),
      handoverPointID: selectedHandoverPoint,
    };

    setSelectedRows && setSelectedRows([]);
    handleSubmit(payload);
  };

  return (
    <Row gutter={[8, 8]}>
      <Col span={24}>
        <Row gutter={[8, 8]}>
          <Col>Set Handover Point</Col>
        </Row>
        <Row gutter={[8, 8]}>
          <Col span={24}>
            These items will be submitted to the handover point you have chosen.
          </Col>
          <Col span={24}>
            <Row gutter={[0, 0]} className="ptm-scrollable">
              {orders?.map((order, index) => {
                return (
                  <Col span={24} key={index}>
                    {order?.orderCode}
                  </Col>
                );
              })}
            </Row>
          </Col>
        </Row>
        <Row gutter={[0, 0]}>
          <Col span={24}>Select handover point: </Col>
          <Col span={24}>
            <SelectInput
              showSearch
              isRequired={true}
              onChange={(value: string | string[]) =>
                handleSelectChange(value, "handoverPoint")
              }
              options={handoverOptions?.map((place) => {
                return {
                  label: place.locationName || "",
                  value: place._id,
                };
              })}
              filterOption={(inputValue, option) =>
                option.label.toLowerCase().indexOf(inputValue.toLowerCase()) >=
                0
              }
            />
          </Col>
        </Row>
        <Row gutter={[8, 8]}>
          <Col span={24}>
            <Button
              type={"primary"}
              label={t("action.confirm")}
              onClick={() => {
                onSubmit();
              }}
            />
            <Button
              className="cancel-btn"
              type={"default"}
              label={t("action.cancel")}
              onClick={handleCancel}
            />
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

interface BatchesViewPage {
  info?: any;
  onBreakBatches?: any;
  handoverOptions: any[];
  vehicleOptions: any[];
  onAssignDriver: any;
}
const ParcelTrackingBatchesViewComponent: React.FC<BatchesViewPage> = ({
  info,
  onBreakBatches,
  handoverOptions,
  vehicleOptions,
  onAssignDriver,
}) => {
  const { t } = useTranslation();
  const { roleTypes } = useSession();
  const history = useHistory();
  const [selectedRows, setSelectedRows] = useState<any>([]);
  const permission = useAccessControlList({
    roleTypes: roleTypes,
    resource: "Delivery_Management",
  });
  const [callModal, setCallModal] = useState<any>(<></>);
  const initialModal: any = {
    open: false,
  };

  const [modal, setModal] = useState<any>(initialModal);

  const showUpdateOption = permission.rights.includes("UPDATE");

  const onBack = () => {
    history.push(`/delivery-management/parcel-tracking?tabKey=2&subTab=3`);
  };

  const rowSelection = {
    onChange: (selectedRows: any[]) => {
      setSelectedRows(selectedRows);
    },
    getCheckboxProps: (record: any) => {
      const allowedStatus = [
        "OPEN",
        "DROP_OFF_IN_PROGRESS",
        "DROPPED_OFF",
        "PICKED_UP",
        "PICKUP_IN_PROGRESS",
      ];

      const isValidStatus = allowedStatus.includes(
        record.orderStatusInfo.orderStatus,
      );

      return {
        disabled: !isValidStatus,
      };
    },
  };

  const handleResetModalState = () => {
    setModal(initialModal);
  };

  const handleSetHandoverPoint = (payload) => {
    onBreakBatches(payload);
    handleResetModalState();
  };

  const handleOpenAssignDriver = () => {
    setCallModal(
      <AssignDriverBoxesModal
        onSubmit={handleAssignDriver}
        vehicleOptions={vehicleOptions}
        boxInfo={info}
        handleCancel={handleResetModalState}
      />,
    );
    setModal({ open: true });
  };

  const handleAssignDriver = (input: any) => {
    onAssignDriver(input);
    handleResetModalState();
  };

  const handleOpenSetHandover = () => {
    const orders = info.orders.filter((order: any) =>
      selectedRows.includes(order._id),
    );

    setCallModal(
      <HandoverPointModal
        orders={orders}
        handoverOptions={handoverOptions}
        handleCancel={handleResetModalState}
        handleSubmit={handleSetHandoverPoint}
        setSelectedRows={setSelectedRows}
      />,
    );
    setModal({ open: true, type: "CREATE" });
  };

  const columns: any = [
    {
      title: t("label.parcelOrderNo").toLocaleUpperCase(),
      dataIndex: "orderCode",
      width: 75,
      ellipsis: true,
      render: (_: any, record: any) => {
        return <span>{record.orderCode}</span>;
      },
    },
    {
      title: t("label.parcelTrackingNo").toLocaleUpperCase(),
      dataIndex: "trackingNumber",
      width: 80,
      ellipsis: true,
      render: (_: any, record: any) => {
        return <span>{record.trackingNumber}</span>;
      },
    },
    {
      title: t("label.deliveryType").toLocaleUpperCase(),
      dataIndex: "additionalCharges",
      width: 90,
      ellipsis: true,
      render: (_: any, record: any) => {
        return !isEmpty(record.additionalCharges) ? (
          map(record.additionalCharges, (type) => {
            if (type.type === "DELIVERY_TYPE") {
              return (
                <React.Fragment>
                  <span>{type.name || <span>&#11835;</span>}</span>
                </React.Fragment>
              );
            }
          })
        ) : (
          <span>Something is super wrong </span>
        );
      },
    },
    {
      title: t("label.customer").toLocaleUpperCase(),
      dataIndex: ["senderInfo", "fullName"],
      width: 70,
      ellipsis: true,
      render: (_: any, record: any) => {
        return <span>{record.senderInfo.fullName}</span>;
      },
    },
    {
      title: t("label.weight").toLocaleUpperCase(),
      dataIndex: ["orders", "weight"],
      width: 55,
      ellipsis: true,
      render: (_: any, record: any) => {
        return <span>{record.weight}</span>;
      },
    },
    {
      title: t("label.volume").toLocaleUpperCase(),
      dataIndex: "volume",
      width: 55,
      ellipsis: true,
      render: (_: any, record: any) => {
        return <span>{parseInt(record.volume).toLocaleString()}</span>;
      },
    },
    {
      title: t("label.dimensions").toLocaleUpperCase(),
      dataIndex: "dimension",
      width: 85,
      ellipsis: true,
      render: (_: any, record: any) => {
        return (
          <span>
            {record.dimension.length} x {record.dimension?.width} x{" "}
            {record.dimension?.height}
          </span>
        );
      },
    },
    {
      title: t("label.status").toLocaleUpperCase(),
      dataIndex: ["orderStatusInfo", "orderStatus"],
      width: 100,
      ellipsis: true,
      render: (status: any) => {
        return (
          <span className="status-indicator" style={getStatusStyle(status)}>
            {status ? toCapitalisedSpaced(status) : "-"}
          </span>
        );
      },
    },
  ];

  const driverColumns: any = [
    {
      title: t("label.driverCompany").toLocaleUpperCase(),
      dataIndex: ["assignedDriver"],
      width: 60,
      ellipsis: true,
      render: (assignedDriver: string) => {
        return (
          <span>
            {assignedDriver
              ? assignedDriver === "THIRD_PARTY"
                ? "3rd Party"
                : "Diolko"
              : "Not assigned"}
          </span>
        );
      },
    },
    {
      title: t("label.driverName").toLocaleUpperCase(),
      dataIndex: ["assignedDriverData", "fullName"],
      width: 75,
      ellipsis: true,
      render: (_: any, record: any) => {
        return (
          <span>
            {record.assignedDriverData
              ? record.assignedDriverData?.fullName
              : record.assignedDriver || "Not assigned"}
          </span>
        );
      },
    },
  ];

  console.log(info);

  return (
    <div className="dm-put-container">
      <div className="dm-put-header">
        <div className="put-title">{info?.batchCode}</div>
        <div className="put-close">
          <CloseCircleOutlined
            style={{ fontSize: "20px" }}
            onClick={() => {
              onBack();
            }}
            className="cursor-pointer back-action right"
          />
        </div>
      </div>
      <div className="dm-divider"></div>
      {showUpdateOption ? (
        <div className="dm-put-btn-container">
          <Button
            type="primary"
            isDisabled={isEmpty(selectedRows)}
            label={t("action.splitBatch")}
            size="large"
            onClick={handleOpenSetHandover}
          />
        </div>
      ) : null}
      <div className="dm-put-content">
        <div className="dm-put-content-inner">
          <Row gutter={[0, 8]}>
            <Col span={24}>
              <DataTable
                scroll={{ x: true }}
                rowKey={(record: any) => {
                  return record._id;
                }}
                rowSelection={rowSelection}
                columns={columns}
                dataSource={info.orders}
              />
            </Col>
          </Row>
        </div>
      </div>
      <div className="dm-put-content dm-bvc-outer">
        <label className="title"> {t("label.driverDetails")} </label>
        <Row gutter={[16, 0]}>
          <Col flex="1">
            <DataTable
              rowKey={(record: any) => {
                return record._id;
              }}
              columns={driverColumns}
              dataSource={[info]}
            />
          </Col>
          <Col>
            {showUpdateOption && info.status != "CLOSED" && (
              <Button
                isLoading={status === "loading"}
                type="primary"
                label={t("action.assignBoxDriver")}
                size="large"
                onClick={handleOpenAssignDriver}
              />
            )}
          </Col>
        </Row>
      </div>
      <BasicModal
        className="modal"
        isOpen={modal.open}
        isClosable={true}
        isMaskClosable={false}
        onClose={() => {
          handleResetModalState();
        }}
      >
        {callModal}
      </BasicModal>
    </div>
  );
};

export default ParcelTrackingBatchesViewComponent;
